import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import ChatIcon from '@atlaskit/icon/core/ai-chat';
import ScorecardIcon from '@atlaskit/icon/core/scorecard';
import SearchIcon from '@atlaskit/icon/core/search';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import { useIntl } from 'react-intl';

import { AnalyticsSideNavItem } from '@townsquare/analytics-nav4';
import { useDashboardExperienceStatus } from '@townsquare/dashboards-view/hooks/useDashboardExperienceStatus';
import { useDiscoverPath } from '@townsquare/discover-view/hooks/use-discover-path';
import { filterUndefined } from '@townsquare/filter-type';
import { SideNavItem } from '@townsquare/griffin-navigation/types';
import { SideNavProductItem } from '@townsquare/navigation-config/nav4';
import { useNoOrgExperience } from '@townsquare/org-context';
import { useRouteDependencies } from '@townsquare/router/primitives/dependencies';
import { matchRoute } from '@townsquare/router/primitives/match-route';
import { generatePath } from '@townsquare/router/primitives/path';
import { useRouter } from '@townsquare/router/useRouter';
import { agentsRoute } from '@townsquare/routes/agents';
import { rovoSearchRoute } from '@townsquare/routes/rovo-search-view';
import { searchRoute } from '@townsquare/routes/search';
import { fg } from '@townsquare/stat-sig/gate';
import { useNoWorkspaceExperience } from '@townsquare/workspace-store';

import { RovoChatNavItem } from './RovoChatNavItem';
import { RovoMoreMenu } from './RovoMoreMenu';
import { RovoTasksNavItem } from './RovoTasksNavItem';
import { useSideNavItemsCore } from './use-side-nav-items';
import { useStatusUpdatesPath } from './use-status-updates-path';
import { useTagsDirectoryRoute } from './use-tags-directory-route';

export const useRovoSideNavItems = (): SideNavItem[] => {
	const intl = useIntl();
	const noOrgExperience = useNoOrgExperience();
	const [routerStore] = useRouter();
	const routeDependencies = useRouteDependencies();

	const { recent, starred, notifications, discover, updates, tags, dashboards } =
		useSideNavItemsCore();

	const { isDiscoverEnabled } = useDiscoverPath();
	const { isStatusUpdatesEnabled } = useStatusUpdatesPath();
	const { isTagsDirectoryEnabled } = useTagsDirectoryRoute();

	const hasNoWorkspace = useNoWorkspaceExperience();
	const { isOptedIn: isDashboardOptedIn } = useDashboardExperienceStatus();
	const isDashboardsEnabled =
		!hasNoWorkspace && fg('atlas_enable_dashboard_route') && isDashboardOptedIn;

	return [
		{
			id: SideNavProductItem.CHAT,
			analyticsSubject: AnalyticsSideNavItem.CHAT,
			name: intl.formatMessage({
				id: 'townsquare.home.use-rovo-side-nav-items.chat',
				description: 'Side nav button to open chat page',
				defaultMessage: 'Chat',
			}),
			Icon: ChatIcon,
			Component: RovoChatNavItem,
		},
		{
			id: SideNavProductItem.AGENTS,
			analyticsSubject: AnalyticsSideNavItem.AGENTS,
			path: generatePath(agentsRoute, routeDependencies),
			matches: [agentsRoute],
			name: intl.formatMessage({
				id: 'townsquare.atlassian-home.side-nav.agents',
				description: 'Side nav button to open agents page',
				defaultMessage: 'Agents',
			}),

			testId: 'side-nav-agents',
			Icon: AiAgentIcon,
		},
		fg('rovo_chat_enable_tasks')
			? {
					id: SideNavProductItem.TASKS,
					analyticsSubject: AnalyticsSideNavItem.TASKS,
					name: intl.formatMessage({
						id: 'townsquare.home.use-rovo-side-nav-items.tasks',
						description: 'Side nav button to open tasks page',
						defaultMessage: 'Tasks',
					}),
					Icon: ScorecardIcon,
					Component: RovoTasksNavItem,
				}
			: undefined,
		recent,
		notifications,
		// Allow items in the More menu to appear at the bottom of the list if selected
		...[
			{
				id: SideNavProductItem.SEARCH,
				analyticsSubject: AnalyticsSideNavItem.SEARCH,
				path: generatePath(rovoSearchRoute, routeDependencies),
				matches: [searchRoute, rovoSearchRoute],
				name: intl.formatMessage({
					id: 'townsquare.atlassian-home.side-nav.search',
					description: 'Side nav button to open search page',
					defaultMessage: 'Search',
				}),

				testId: 'side-nav-search',
				Icon: SearchIcon,
			},
			!noOrgExperience ? starred : undefined,
			isDiscoverEnabled ? discover : undefined,
			isStatusUpdatesEnabled ? updates : undefined,
			isTagsDirectoryEnabled ? tags : undefined,
			isDashboardsEnabled ? dashboards : undefined,
		]
			// Exclude sidebar items which are not enabled
			.filter(filterUndefined)
			// Only include sidebar items that are currently selected
			.filter(
				(item) =>
					'matches' in item &&
					item.matches &&
					!!matchRoute(item.matches, routerStore.location.pathname, undefined),
			),
		{
			id: SideNavProductItem.MORE,
			analyticsSubject: AnalyticsSideNavItem.MORE,
			matches: [],
			name: intl.formatMessage({
				id: 'townsquare.atlassian-home.side-nav.more',
				defaultMessage: 'More',
				description: 'Side nav button to open more page',
			}),
			testId: 'side-nav-more',
			Icon: ShowMoreHorizontalIcon,
			Component: RovoMoreMenu,
		},
	].filter(filterUndefined);
};
